/**
 * Portfolio Archive Filter Module
 * Handles filtering, search, and load more for portfolio archive page
 */

let args = {};
let page = 1;
let maxPages = 1;
let selectedTaxonomies = {};
let loader = null;
let loadMoreButton = null;
let postsContainer = null;
let searchInput = null;
let postsPerPageDesktop = 6;
let postsPerPageMobile = 3;
let initialLoadDone = false;

/**
 * Check if current viewport is mobile
 */
function isMobileViewport() {
    return window.innerWidth < 576;
}

/**
 * Get current posts per page based on viewport
 */
function getCurrentPostsPerPage() {
    return isMobileViewport() ? postsPerPageMobile : postsPerPageDesktop;
}

/**
 * Initialize Portfolio Filter
 */
export function initPortfolioFilter() {
    const argsInput = document.getElementById('portfolio-args');
    if (!argsInput) return;

    args = JSON.parse(argsInput.value);
    loader = document.getElementById('ajax-loader');
    loadMoreButton = document.getElementById('load-more');
    postsContainer = document.getElementById('posts-container');
    searchInput = document.getElementById('search-input');
    
    // Get posts per page values
    const desktopInput = document.getElementById('portfolio-posts-per-page-desktop');
    const mobileInput = document.getElementById('portfolio-posts-per-page-mobile');
    if (desktopInput) postsPerPageDesktop = parseInt(desktopInput.value, 10) || 6;
    if (mobileInput) postsPerPageMobile = parseInt(mobileInput.value, 10) || 3;
    
    // Get initial max pages
    const maxPagesInput = document.getElementById('portfolio-max-pages');
    if (maxPagesInput) {
        maxPages = parseInt(maxPagesInput.value, 10) || 1;
    }

    bindEvents();
    getSelectedTaxonomies();
    
    // Check if we need to reload for mobile (server sent desktop count)
    const currentPostsPerPage = getCurrentPostsPerPage();
    if (currentPostsPerPage !== args.posts_per_page) {
        args.posts_per_page = currentPostsPerPage;
        refreshList();
    } else {
        // Set initial load more button visibility
        if (maxPages <= 1) {
            loadMoreButtonHide();
        }
    }
    
    initialLoadDone = true;
}

function bindEvents() {
    setupCustomTaxonomySelects();

    // Taxonomy filter change
    const taxonomySelects = document.querySelectorAll('.taxonomy-select');
    taxonomySelects.forEach(select => {
        select.addEventListener('change', () => {
            getSelectedTaxonomies();
            refreshList();
        });
    });

    // Search filter with debounce
    let typingTimer;
    const debounceTime = 300;

    if (searchInput) {
        searchInput.addEventListener('input', () => {
            clearTimeout(typingTimer);
            typingTimer = setTimeout(() => {
                refreshList();
            }, debounceTime);
        });
    }

    // Load more button click
    if (loadMoreButton) {
        loadMoreButton.addEventListener('click', (e) => {
            e.preventDefault();
            page++;
            loadMoreLoading();
            getPortfolioPosts(false);
        });
    }
}

/**
 * Initialize the styled taxonomy dropdowns while keeping the native selects
 * as the source of truth for portfolio filtering.
 */
function setupCustomTaxonomySelects() {
    const dropdowns = document.querySelectorAll('.custom-taxonomy-select');

    const closeDropdown = (dropdown, returnFocus = false) => {
        const trigger = dropdown.querySelector('.custom-select-trigger');
        const menu = dropdown.querySelector('.custom-select-menu');
        const chevron = dropdown.querySelector('.custom-select-chevron');

        menu?.classList.add('hidden');
        chevron?.classList.remove('rotate-180');
        trigger?.setAttribute('aria-expanded', 'false');

        if (returnFocus) trigger?.focus();
    };

    const closeOtherDropdowns = currentDropdown => {
        dropdowns.forEach(dropdown => {
            if (dropdown !== currentDropdown) closeDropdown(dropdown);
        });
    };

    dropdowns.forEach(dropdown => {
        const select = dropdown.querySelector('.taxonomy-select');
        const trigger = dropdown.querySelector('.custom-select-trigger');
        const label = dropdown.querySelector('.custom-select-label');
        const menu = dropdown.querySelector('.custom-select-menu');
        const chevron = dropdown.querySelector('.custom-select-chevron');
        const options = Array.from(dropdown.querySelectorAll('.custom-select-option'));

        if (!select || !trigger || !label || !menu || options.length === 0) return;

        const updateSelectedOption = () => {
            const selectedOption = options.find(option => option.dataset.value === select.value) || options[0];
            label.textContent = selectedOption.textContent.trim();

            options.forEach(option => {
                const isSelected = option === selectedOption;
                option.setAttribute('aria-selected', String(isSelected));
                option.classList.toggle('bg-[#FFF3E6]', isSelected);
                option.classList.toggle('font-medium', isSelected);
            });
        };

        const openDropdown = () => {
            closeOtherDropdowns(dropdown);
            menu.classList.remove('hidden');
            chevron?.classList.add('rotate-180');
            trigger.setAttribute('aria-expanded', 'true');
        };

        trigger.addEventListener('click', () => {
            if (menu.classList.contains('hidden')) {
                openDropdown();
            } else {
                closeDropdown(dropdown);
            }
        });

        trigger.addEventListener('keydown', event => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                openDropdown();
                const selectedOption = options.find(option => option.dataset.value === select.value) || options[0];
                selectedOption.focus();
            }
        });

        options.forEach(option => {
            option.addEventListener('click', () => {
                select.value = option.dataset.value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
                closeDropdown(dropdown, true);
            });
        });

        menu.addEventListener('keydown', event => {
            const currentIndex = options.indexOf(document.activeElement);

            if (event.key === 'Escape') {
                event.preventDefault();
                closeDropdown(dropdown, true);
            } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const direction = event.key === 'ArrowDown' ? 1 : -1;
                const nextIndex = (currentIndex + direction + options.length) % options.length;
                options[nextIndex].focus();
            } else if (event.key === 'Home' || event.key === 'End') {
                event.preventDefault();
                options[event.key === 'Home' ? 0 : options.length - 1].focus();
            }
        });

        select.addEventListener('change', updateSelectedOption);
        updateSelectedOption();
    });

    document.addEventListener('click', event => {
        dropdowns.forEach(dropdown => {
            if (!dropdown.contains(event.target)) closeDropdown(dropdown);
        });
    });
}

function loaderShow() {
    if (loader) loader.style.display = 'block';
    if (postsContainer) postsContainer.style.display = 'none';
}

function loaderHide() {
    if (loader) loader.style.display = 'none';
    if (postsContainer) postsContainer.style.display = 'block';
}

function loadMoreButtonShow() {
    if (loadMoreButton && loadMoreButton.parentElement) {
        loadMoreButton.parentElement.style.display = 'block';
    }
}

function loadMoreButtonHide() {
    if (loadMoreButton && loadMoreButton.parentElement) {
        loadMoreButton.parentElement.style.display = 'none';
    }
}

function resetPagination() {
    page = 1;
}

function refreshList() {
    resetPagination();
    loaderShow();
    getPortfolioPosts(true);
}

function getSelectedTaxonomies() {
    selectedTaxonomies = {};
    const taxonomySelects = document.querySelectorAll('.taxonomy-select');
    taxonomySelects.forEach(select => {
        const taxonomy = select.dataset.taxonomy;
        const selectedValue = select.value;
        if (selectedValue !== '' && taxonomy) {
            selectedTaxonomies[taxonomy] = selectedValue;
        }
    });
}

function loadMoreEnable() {
    if (!loadMoreButton) return;
    loadMoreButton.classList.remove('loading');
    loadMoreButton.style.pointerEvents = '';
    loadMoreButton.style.opacity = '';
    const spinner = loadMoreButton.querySelector('.btn-spinner');
    const btnText = loadMoreButton.querySelector('.btn-text');
    if (spinner) spinner.style.display = 'none';
    if (btnText) btnText.textContent = loadMoreButton.dataset.text || 'Load More';
    loadMoreButton.blur();
}

function loadMoreLoading() {
    if (!loadMoreButton) return;
    loadMoreButton.classList.add('loading');
    loadMoreButton.style.pointerEvents = 'none';
    loadMoreButton.style.opacity = '0.8';
    const spinner = loadMoreButton.querySelector('.btn-spinner');
    const btnText = loadMoreButton.querySelector('.btn-text');
    if (spinner) spinner.style.display = 'inline-block';
    if (btnText) btnText.textContent = 'Loading...';
}

function getPortfolioPosts(clearContainer) {
    const startTime = Date.now();
    
    // Get AJAX config from localized data
    const ajaxConfig = window.vtPortfolioFilter;
    if (!ajaxConfig) {
        console.error('Portfolio filter: AJAX config not found');
        return;
    }

    const formData = new FormData();
    formData.append('action', 'get_portfolio_posts_cards');
    formData.append('nonce', ajaxConfig.nonce);
    formData.append('query', JSON.stringify(args));
    formData.append('paged', page);
    formData.append('taxonomies', JSON.stringify(selectedTaxonomies));
    formData.append('s', searchInput ? searchInput.value : '');

    fetch(ajaxConfig.ajaxurl, {
        method: 'POST',
        body: formData
    })
    .then(response => response.json())
    .then(response => {
        const endTime = Date.now();
        const elapsedTime = endTime - startTime;
        const remainingTime = Math.max(500 - elapsedTime, 0);

        setTimeout(() => {
            loaderHide();
            loadMoreEnable();

            if (response.success) {
                const jsonObj = response.data;
                maxPages = jsonObj.max_page || 1;
                
                if (page >= maxPages) {
                    loadMoreButtonHide();
                } else {
                    loadMoreButtonShow();
                }
                handleData(clearContainer, jsonObj);
            } else {
                maxPages = 1;
                loadMoreButtonHide();
                if (clearContainer && postsContainer) {
                    postsContainer.innerHTML = response.data.html;
                }
            }
        }, remainingTime);
    })
    .catch(error => {
        console.error('Portfolio filter error:', error);
        loaderHide();
        loadMoreEnable();
    });
}

function handleData(clearContainer, jsonObj) {
    if (!postsContainer) return;
    
    if (clearContainer) {
        postsContainer.innerHTML = jsonObj.html;
    } else {
        // Create temp container and animate new items
        const temp = document.createElement('div');
        temp.innerHTML = jsonObj.html;
        let delay = 0;
        
        Array.from(temp.children).forEach(item => {
            item.style.opacity = '0';
            item.style.transform = 'translateY(20px)';
            item.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
            item.style.transitionDelay = delay + 'ms';
            postsContainer.appendChild(item);

            // Trigger animation
            setTimeout(() => {
                item.style.opacity = '1';
                item.style.transform = 'translateY(0)';
            }, 10);

            delay += 100;
        });
    }
}

